import React, { useEffect, useState } from 'react';

export const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState(0);

  const loadingMessages = [
    'Inicializando sistema...',
    'Carregando componentes...',
    'Preparando interface...',
    'Quase pronto...'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsVisible(false);
            if (onLoadingComplete) onLoadingComplete();
          }, 600);
          return 100;
        }
        const newProgress = prev + Math.random() * 8 + 3;
        setLoadingPhase(Math.min(Math.floor(newProgress / 25), 3));
        return newProgress;
      });
    }, 150);

    return () => clearInterval(timer);
  }, [onLoadingComplete]);

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-all duration-700 ${progress >= 100 ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}>
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-[#0a1628] to-slate-950" />
      
      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Floating Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center">
        
        {/* Logo with Glow Effect */}
        <div className="relative mb-12">
          <div className="absolute inset-0 blur-2xl opacity-50">
            <img 
              src="/logo-pcs.png" 
              alt="" 
              className="h-20 w-auto opacity-60"
            />
          </div>
          <img 
            src="/logo-pcs.png" 
            alt="PCS Dutos e Exaustores" 
            className="h-20 w-auto relative z-10 drop-shadow-2xl"
          />
        </div>

        {/* Animated Loader Ring */}
        <div className="relative w-32 h-32 mb-10">
          {/* Outer spinning ring */}
          <svg className="absolute inset-0 w-full h-full animate-spin" style={{ animationDuration: '3s' }}>
            <defs>
              <linearGradient id="spinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity="1" />
                <stop offset="50%" stopColor="#6366f1" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle
              cx="64"
              cy="64"
              r="58"
              fill="none"
              stroke="url(#spinGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="180 180"
            />
          </svg>

          {/* Inner counter-spinning ring */}
          <svg className="absolute inset-0 w-full h-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}>
            <defs>
              <linearGradient id="spinGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle
              cx="64"
              cy="64"
              r="48"
              fill="none"
              stroke="url(#spinGradient2)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="120 180"
            />
          </svg>

          {/* Center Progress Circle */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="rgba(30, 58, 95, 0.5)"
                  strokeWidth="4"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="url(#progressGradient)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - progress / 100)}`}
                  style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#0ea5e9" />
                    <stop offset="100%" stopColor="#6366f1" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  {Math.min(Math.round(progress), 100)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Message */}
        <div className="text-center space-y-3">
          <p className="text-slate-300 text-lg font-medium tracking-wide animate-pulse">
            {loadingMessages[loadingPhase]}
          </p>
          
          {/* Animated Dots */}
          <div className="flex items-center justify-center space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
              />
            ))}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-10 w-80">
          <div className="h-1.5 bg-slate-800/80 rounded-full overflow-hidden backdrop-blur-sm border border-slate-700/50">
            <div 
              className="h-full rounded-full transition-all duration-300 ease-out relative overflow-hidden"
              style={{ 
                width: `${Math.min(progress, 100)}%`,
                background: 'linear-gradient(90deg, #0ea5e9, #6366f1, #0ea5e9)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s linear infinite'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Branding */}
      <div className="absolute bottom-8 text-center">
        <p className="text-slate-600 text-xs tracking-widest uppercase">
          Qualidade e Excelência em Ventilação Industrial
        </p>
      </div>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
